import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import PageHeader from "../../components/common/PageHeader";
import TableGrid from "../../components/TableBooking/TableGrid";
import CustomerModal from "../../components/TableBooking/CustomerModal";
import ManageTablesModal from "../../components/TableBooking/ManageTablesModal";
import AddTableModal from "../../components/TableBooking/AddTableModal";
import ViewQrModal from "../../components/TableBooking/ViewQrModal";
import IncomingRequestsModal from "../../components/TableBooking/IncomingQrRequests";
import AllocationModal from "../../components/TableBooking/AllocationModal";
import ReservationModal from "../../components/TableBooking/ReservationModal";
import ReservationListModal from "../../components/TableBooking/ReservationListModal";
import WaiterAssignModal from "../../components/TableBooking/WaiterAssignModal";
import TableStatusLegend from "../../components/TableBooking/TableStatusLegend";
import ActionButtons from "../../components/TableBooking/ActionButtons";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import {
  getRestaurantTodayDate,
  getRestaurantCurrentTime,
} from "../../utils/dateTimeUtils";
import { useTableBooking } from "../../hooks/useTableBooking";
import { useReservations } from "../../hooks/useReservations";
import { useIncomingRequests } from "../../hooks/useIncomingRequests";
import { useAllocation } from "../../hooks/useAllocation";

const TableBookingPage = () => {
  const navigate = useNavigate();
  const { restaurantId, timezone } = useRestaurantConfig();
  const role = localStorage.getItem("role");
  const isWaiter = role === "waiter";
  const [qrFeatureEnabled, setQrFeatureEnabled] = useState(true);

  React.useEffect(() => {
    const loadFeatures = async () => {
      try {
        const res = await api.get("/settings/features");
        if (res?.data?.success && res?.data?.features) {
          setQrFeatureEnabled(res.data.features.qrFeature !== false);
        }
      } catch (err) {
        // If settings endpoint fails, default to showing QR
        setQrFeatureEnabled(true);
      }
    };

    loadFeatures();
  }, []);

  // Custom hooks
  const {
    tables,
    lastRefreshed,
    handleToggleTable,
    createOrUpdateTable,
    handleBulkPrintDirect,
  } = useTableBooking(restaurantId);

  const {
    reservations,
    fetchUpcomingReservations,
    handleSubmitReservation: submitReservation,
    handleAcceptReservation,
    handleCancelReservation,
  } = useReservations(restaurantId);

  const {
    incomingRequests,
    incomingLoading,
    waiters,
    fetchIncomingRequests,
    handleAcceptQrRequest,
    handleRejectQrRequest,
  } = useIncomingRequests(restaurantId);

  const { handleSubmitAllocation: submitAllocation } =
    useAllocation(restaurantId);

  // Local state
  const [searchMain, setSearchMain] = useState("");
  const [openAllocation, setOpenAllocation] = useState(false);
  const [openReservation, setOpenReservation] = useState(false);
  const [openReservationList, setOpenReservationList] = useState(false);
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [openManageTables, setOpenManageTables] = useState(false);
  const [openTableForm, setOpenTableForm] = useState(false);
  const [selectedManageTable, setSelectedManageTable] = useState(null);
  const [openQrModal, setOpenQrModal] = useState(false);
  const [qrTable, setQrTable] = useState(null);
  const [openIncomingRequests, setOpenIncomingRequests] = useState(false);
  const [selectedQrRequest, setSelectedQrRequest] = useState(null);
  const [openWaiterModal, setOpenWaiterModal] = useState(false);
  const [selectedWaiter, setSelectedWaiter] = useState("");

  const [allocation, setAllocation] = useState({
    name: "",
    phone: "",
    tableNumber: "",
    customerCount: "",
  });

  const [reservation, setReservation] = useState({
    name: "",
    phone: "",
    tableNumber: "",
    date: "",
    time: "",
    customerCount: "",
  });

  // Table click handler
  const handleTableClick = (table) => {
    if (table.status === "available") {
      setAllocation({
        name: "",
        phone: "",
        tableNumber: table.tableNumber,
        customerCount: "",
      });
      setOpenAllocation(true);
      return;
    }

    if (table.status === "occupied") {
      const formattedCustomers = table.currentAllocations.map((c) => ({
        ...c,
        tableNumber: table.tableNumber,
      }));

      if (formattedCustomers.length === 1) {
        handleOpenOrder(formattedCustomers[0]);
      } else {
        setCustomerData(formattedCustomers);
        setOpenCustomerModal(true);
      }
    }
  };

  // Open order for customer (now a dedicated page)
  const handleOpenOrder = (customer) => {
    setOpenCustomerModal(false);
    navigate(`/table-booking/order/${customer.id}`, { state: { customer } });
  };

  // Submit allocation
  const handleSubmitAllocation = async () => {
    const newCustomer = await submitAllocation(allocation);
    if (newCustomer) {
      setOpenAllocation(false);
      navigate(`/table-booking/order/${newCustomer.id}`, {
        state: { customer: newCustomer },
      });
    }
  };

  // Submit reservation
  const handleSubmitReservation = async () => {
    const success = await submitReservation(reservation);
    if (success) {
      setReservation({
        name: "",
        phone: "",
        tableNumber: "",
        date: "",
        time: "",
        customerCount: "",
      });
      setOpenReservation(false);
    }
  };

  // Assign waiter and accept QR request
  const handleConfirmAcceptQr = async () => {
    const success = await handleAcceptQrRequest(
      selectedQrRequest._id,
      selectedWaiter,
    );
    if (success) {
      setOpenWaiterModal(false);
      fetchIncomingRequests();
    }
  };

  // Open waiter assignment modal
  const handleOpenAssignWaiter = (request) => {
    setSelectedQrRequest(request);
    setSelectedWaiter("");
    setOpenWaiterModal(true);
  };

  // Computed values
  const reservationTables = tables.filter((t) => t.isActive);
  const allocationTables = tables.filter(
    (t) =>
      t.isActive &&
      (t.status === "available" ||
        (t.status === "occupied" && !t.hasAcceptedReservation)),
  );
  const incomingRequestCount = incomingRequests.length;

  return (
    <div className="px-4 pb-4">
      <PageHeader
        showBackButton={true}
        backTo={-1}
        title="Table Booking"
        subtitle={
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
            Manage table allocations and reservations
          </span>
        }
      />

      <ActionButtons
        isWaiter={isWaiter}
        onCustomerAllocation={() => {
          setAllocation({
            name: "",
            phone: "",
            tableNumber: "",
            customerCount: "",
          });
          setOpenAllocation(true);
        }}
        onReservation={() => {
          setReservation((prev) => ({
            ...prev,
            date: getRestaurantTodayDate(timezone),
            time: getRestaurantCurrentTime(timezone),
          }));
          setOpenReservation(true);
        }}
        onReservationList={() => {
          fetchUpcomingReservations();
          setOpenReservationList(true);
        }}
        onManageTables={() => setOpenManageTables(true)}
        onIncomingRequests={() => {
          setOpenIncomingRequests(true);
          fetchIncomingRequests();
        }}
        incomingRequestCount={incomingRequestCount}
      />

      <TableStatusLegend />

      <TableGrid
        tables={tables}
        handleToggleTable={handleToggleTable}
        onTableClick={handleTableClick}
      />

      {/* Customer Selection Modal */}
      <CustomerModal
        open={openCustomerModal}
        onClose={() => setOpenCustomerModal(false)}
        customers={customerData}
        handleOpenOrder={handleOpenOrder}
      />

      {/* Allocation Modal */}
      <AllocationModal
        open={openAllocation}
        onClose={() => setOpenAllocation(false)}
        allocation={allocation}
        setAllocation={setAllocation}
        allocationTables={allocationTables}
        onSubmit={handleSubmitAllocation}
      />

      {/* Reservation Modal */}
      <ReservationModal
        open={openReservation}
        onClose={() => setOpenReservation(false)}
        reservation={reservation}
        setReservation={setReservation}
        reservationTables={reservationTables}
        onSubmit={handleSubmitReservation}
        timezone={timezone}
      />

      {/* Reservation List Modal */}
      <ReservationListModal
        open={openReservationList}
        onClose={() => setOpenReservationList(false)}
        reservations={reservations}
        onAccept={handleAcceptReservation}
        onCancel={handleCancelReservation}
      />

      {/* Manage Tables Modal */}
      <ManageTablesModal
        open={openManageTables}
        onClose={() => setOpenManageTables(false)}
        tables={tables}
        loading={false}
        onAdd={() => {
          setSelectedManageTable(null);
          setOpenManageTables(false);
          setOpenTableForm(true);
        }}
        onEdit={(table) => {
          setSelectedManageTable(table);
          setOpenManageTables(false);
          setOpenTableForm(true);
        }}
        onViewQr={(table) => {
          setQrTable(table);
          setOpenQrModal(true);
        }}
        onBulkPrint={handleBulkPrintDirect}
        qrEnabled={qrFeatureEnabled}
      />

      {/* Add/Edit Table Modal */}
      <AddTableModal
        open={openTableForm}
        onClose={() => setOpenTableForm(false)}
        mode={selectedManageTable ? "edit" : "add"}
        initialData={selectedManageTable}
        onSave={createOrUpdateTable}
      />

      {/* View QR Modal */}
      <ViewQrModal
        open={openQrModal}
        onClose={() => {
          setOpenQrModal(false);
          setQrTable(null);
        }}
        table={qrTable}
      />

      {/* Incoming Requests Modal */}
      <IncomingRequestsModal
        open={openIncomingRequests}
        onClose={() => setOpenIncomingRequests(false)}
        requests={incomingRequests}
        loading={incomingLoading}
        onAccept={handleOpenAssignWaiter}
        onReject={handleRejectQrRequest}
      />

      {/* Waiter Assignment Modal */}
      <WaiterAssignModal
        open={openWaiterModal}
        onClose={() => setOpenWaiterModal(false)}
        waiters={waiters}
        selectedWaiter={selectedWaiter}
        setSelectedWaiter={setSelectedWaiter}
        onConfirm={handleConfirmAcceptQr}
      />
    </div>
  );
};

export default TableBookingPage;
